import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, MapPin, Heart, Zap } from 'lucide-react';
import axios from 'axios';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { useComparison } from '../contexts/ComparisonContext';
import VerifiedBadge from './VerifiedBadge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolveImg = (url) => url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const VehicleCard = ({ vehicle }) => {
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [dealerInfo, setDealerInfo] = useState(null);
  const { rate: exchangeRate } = useExchangeRate();
  const { addVehicle, removeVehicle, isSelected } = useComparison();
  const isDirect = vehicle.listing_type === 'direct_sale';
  const inComparison = isSelected(vehicle.vehicle_id);

  useEffect(() => {
    // Fetch dealer info if dealer_id exists
    const fetchDealerInfo = async () => {
      if (vehicle.dealer_id) {
        try {
          const response = await axios.get(`${API}/dealers/profile/${vehicle.dealer_id}`);
          setDealerInfo(response.data);
        } catch (error) {
          console.error('Error fetching dealer info:', error);
        }
      }
    };
    
    fetchDealerInfo();
    
    // Check if vehicle is in watchlist
    const checkWatchlistStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(`${API}/watchlist`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        
        const watchlistVehicles = response.data.vehicles || [];
        const isInWatchlist = watchlistVehicles.some(v => v.vehicle_id === vehicle.vehicle_id);
        setIsWatchlisted(isInWatchlist);
      } catch (error) {
        console.error('Error checking watchlist:', error);
      }
    };
    
    checkWatchlistStatus();
  }, [vehicle.vehicle_id]);

  useEffect(() => {
    if (isDirect) return;
    const fetchAuction = async () => {
      try {
        const response = await axios.get(`${API}/auctions/${vehicle.vehicle_id}/info`);
        setAuction(response.data);
      } catch (error) {
        console.error('Error fetching auction:', error);
      }
    };
    fetchAuction();
  }, [vehicle.vehicle_id, isDirect]);

  useEffect(() => {
    if (!auction) return;

    const interval = setInterval(() => {
      const end = new Date(auction.end_time);
      const now = new Date();
      const diff = end - now;

      if (diff <= 0) {
        setTimeLeft('Auction Ended');
        clearInterval(interval);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h`);
      } else {
        setTimeLeft(`${hours}h ${minutes}m`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [auction]);

  const toggleWatchlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login to add to watchlist');
        return;
      }

      if (isWatchlisted) {
        await axios.delete(`${API}/watchlist/${vehicle.vehicle_id}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setIsWatchlisted(false);
      } else {
        await axios.post(`${API}/watchlist/${vehicle.vehicle_id}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setIsWatchlisted(true);
      }
    } catch (error) {
      console.error('Watchlist error:', error);
    }
  };

  const toggleComparison = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (inComparison) {
      removeVehicle(vehicle.vehicle_id);
      toast.success('Removed from comparison');
    } else {
      const result = addVehicle(vehicle);
      if (result.success) {
        toast.success('Added to comparison');
      } else {
        toast.error(result.message);
      }
    }
  };

  return (
    <Link
      to={`/vehicle/${vehicle.vehicle_id}`}
      data-testid={`vehicle-card-${vehicle.vehicle_id}`}
      className="bg-surface border border-white/10 rounded-sm overflow-hidden group hover:border-primary/50 transition-all duration-300 relative block"
    >
      {/* Image */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={resolveImg(vehicle.images[0])}
          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* Condition Badge */}
        <div className={`absolute top-4 left-4 px-3 py-1 rounded-sm text-xs font-bold uppercase ${
          vehicle.condition === 'clean' ? 'bg-green-600' :
          vehicle.condition === 'salvage' ? 'bg-yellow-600' : 'bg-red-600'
        }`}>
          {vehicle.condition}
        </div>

        {/* Listing Type Badge */}
        <div className={`absolute top-4 left-24 px-3 py-1 rounded-sm text-xs font-bold uppercase ${isDirect ? 'bg-blue-600' : 'bg-primary'}`}>
          {isDirect ? 'For Sale' : 'Auction'}
        </div>

        {/* Featured Badge */}
        {vehicle.is_featured && (
          <div className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-sm text-xs font-bold uppercase flex items-center space-x-1 shadow-lg">
            <Zap className="w-3 h-3" />
            <span>Featured</span>
          </div>
        )}

        {/* Watchlist Button */}
        <button
          onClick={toggleWatchlist}
          data-testid="watchlist-toggle"
          className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-primary transition-colors"
        >
          <Heart className={`w-5 h-5 ${isWatchlisted ? 'fill-primary' : ''}`} />
        </button>

        {/* Compare Checkbox */}
        <button
          onClick={toggleComparison}
          data-testid="compare-toggle"
          className="absolute bottom-4 left-4 glass px-3 py-2 rounded-sm hover:bg-primary/20 transition-colors flex items-center gap-2"
        >
          <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
            inComparison ? 'bg-primary border-primary' : 'border-white/30'
          }`}>
            {inComparison && (
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-xs font-medium">Compare</span>
        </button>

        {/* Time Left */}
        {!isDirect && timeLeft && (
          <div className="absolute bottom-4 right-4 glass px-3 py-1 rounded-sm flex items-center space-x-2">
            <Clock className="w-4 h-4 text-primary" />
            <span className="text-sm font-mono font-bold">{timeLeft}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-heading text-xl font-bold mb-2">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h3>

        <div className="flex items-center space-x-4 text-sm text-textSecondary mb-4">
          <span>{vehicle.mileage.toLocaleString()} miles</span>
          <span>•</span>
          <span className="flex items-center space-x-1">
            <MapPin className="w-4 h-4" />
            <span>{vehicle.location}</span>
          </span>
        </div>

        {/* Dealer Info */}
        {dealerInfo && (
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/dealer/${dealerInfo.dealer_id}`);
              }}
              className="flex items-center space-x-2 hover:text-primary transition-colors text-left"
            >
              <span className="text-sm text-textSecondary">Sold by:</span>
              <span className="text-sm font-medium underline">{dealerInfo.name}</span>
            </button>
            {dealerInfo.is_verified && (
              <VerifiedBadge size="xs" showText={false} />
            )}
          </div>
        )}

        {isDirect ? (
          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <div>
              <div className="text-xs text-textSecondary">Sale Price</div>
              {vehicle.currency === 'NGN' ? (
                <div className="text-2xl font-heading font-bold text-primary">
                  ₦{vehicle.price?.toLocaleString()}
                </div>
              ) : (
                <>
                  <div className="text-2xl font-heading font-bold text-primary">
                    ${vehicle.price?.toLocaleString()}
                  </div>
                  <div className="text-xs text-textSecondary mt-1">
                    ≈ ₦{((vehicle.price || 0) * exchangeRate).toLocaleString()} (approx)
                  </div>
                </>
              )}
            </div>
            <div className="text-right">
              <span className="px-3 py-1 bg-blue-600 rounded-sm text-xs font-bold">BUY NOW</span>
            </div>
          </div>
        ) : auction ? (
          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <div>
              <div className="text-xs text-textSecondary">Current Bid</div>
              <div className="text-2xl font-heading font-bold text-primary">
                ${auction.current_bid.toLocaleString()}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-textSecondary">{auction.bid_count} bids</div>
              {auction.buy_now_price && (
                <div className="text-sm text-textSecondary">Buy Now: ${auction.buy_now_price.toLocaleString()}</div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </Link>
  );
};

export default VehicleCard;